import React, { useState, useEffect } from 'react';
import Icon from '../../components/Icon';

const INTENSITY_TIERS = [
  { min: 80, label: 'Personal',  color: '#f87171', bg: 'rgba(220,38,38,0.12)',  icon: '💢' },
  { min: 60, label: 'Heated',    color: '#fb923c', bg: 'rgba(249,115,22,0.12)', icon: '🔥' },
  { min: 35, label: 'Active',    color: '#60a5fa', bg: 'rgba(59,130,246,0.12)', icon: '⚔️' },
  { min: 0,  label: 'Developing',color: '#9ca3af', bg: 'rgba(107,114,128,0.1)', icon: '👀' },
];

function getTier(score) {
  return INTENSITY_TIERS.find(t => score >= t.min) || INTENSITY_TIERS[INTENSITY_TIERS.length - 1];
}

function IntensityBar({ score }) {
  const tier = getTier(score);
  return (
    <div className="lor-rivalry-intensity-row">
      <div className="lor-pr-bar-track" style={{ flex: 1, height: 6 }}>
        <div
          className="lor-pr-bar-fill"
          style={{ width: `${score}%`, background: tier.color, height: '100%', borderRadius: 100 }}
        />
      </div>
      <span style={{ fontSize: '0.72rem', color: tier.color, fontWeight: 700, minWidth: 32 }}>
        {Math.round(score)}
      </span>
    </div>
  );
}

function RivalryCard({ rivalry }) {
  const [open, setOpen] = useState(false);
  const tier = getTier(rivalry.intensity_score);
  const streak = rivalry.h2h_streak || {};
  const last5  = rivalry.last5 || [];
  const moments = rivalry.biggest_moments || [];
  const playoffs = rivalry.playoff_games || [];

  return (
    <div
      className="lor-rivalry-card lor-card"
      style={{ borderColor: `${tier.color}44`, borderWidth: 1, borderStyle: 'solid' }}
    >
      <div className="lor-rivalry-card-header" onClick={() => setOpen(o => !o)}>
        <div className="lor-rivalry-badge" style={{ color: tier.color, background: tier.bg }}>
          {tier.icon === '⚔️' ? <><Icon emoji="⚔️" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> {tier.label}</> : `${tier.icon} ${tier.label}`}
        </div>
        <div className="lor-rivalry-managers">
          <span className="lor-rivalry-manager-name">{rivalry.manager_a}</span>
          <span className="lor-rivalry-vs">vs</span>
          <span className="lor-rivalry-manager-name">{rivalry.manager_b}</span>
        </div>
        <div className="lor-rivalry-record-summary">
          <span className="lor-wins">{rivalry.record_a}</span>
          <span style={{ color: '#444', margin: '0 6px' }}>·</span>
          <span className="lor-losses">{rivalry.record_b}</span>
        </div>
        <span className="lor-history-chevron">{open ? '▲' : '▼'}</span>
      </div>

      <div className="lor-rivalry-card-meta">
        <span className="lor-rivalry-meta-item">{rivalry.total_games} matchups</span>
        {rivalry.playoff_meetings > 0 && (
          <span className="lor-rivalry-meta-item"><Icon emoji="🏆" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> {rivalry.playoff_meetings} playoff meeting{rivalry.playoff_meetings > 1 ? 's' : ''}</span>
        )}
        {rivalry.is_championship_pairing && (
          <span className="lor-rivalry-meta-item lor-gold"><Icon emoji="👑" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Championship meeting</span>
        )}
        {streak.count > 1 && (
          <span className="lor-rivalry-meta-item">{streak.manager} Sleeper-era streak W{streak.count}</span>
        )}
      </div>

      <div className="lor-rivalry-intensity-section">
        <span className="lor-history-section-label" style={{ marginBottom: 4 }}>Intensity</span>
        <IntensityBar score={rivalry.intensity_score} />
      </div>

      {open && (
        <div className="lor-rivalry-detail">
          {moments.length > 0 && (
            <div className="lor-rivalry-moments">
              <div className="lor-history-section-label">Biggest Moments</div>
              {moments.map((m, i) => (
                <div key={i} className="lor-rivalry-moment-row">
                  <span className="lor-rivalry-moment-dot" />
                  {m}
                </div>
              ))}
            </div>
          )}

          {playoffs.length > 0 && (
            <div className="lor-rivalry-playoffs">
              <div className="lor-history-section-label">Playoff Meetings</div>
              {playoffs.map((g, i) => (
                <div key={i} className="lor-h2h-playoff-game">
                  <span className="lor-h2h-playoff-year">{g.season}</span>
                  <span className="lor-history-playoff-winner"><Icon emoji="🏆" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> {g.winner}</span>
                  <span className="lor-history-playoff-score">{g.w_pts} – {g.l_pts}</span>
                  <span className="lor-history-playoff-loser">{g.loser}</span>
                </div>
              ))}
            </div>
          )}

          {last5.length > 0 && (
            <div className="lor-rivalry-last5">
              <div className="lor-history-section-label">Last {last5.length} Matchups</div>
              {[...last5].reverse().map((g, i) => (
                <div key={i} className="lor-h2h-last5-game">
                  <span className="lor-h2h-last5-meta">{g.season} Wk {g.week}</span>
                  <span className="lor-h2h-last5-score">{g.score}</span>
                  <span className="lor-h2h-last5-winner">W: {g.winner}</span>
                </div>
              ))}
            </div>
          )}

          {rivalry.biggest_win && (
            <div className="lor-rivalry-notable-row">
              <span className="lor-history-section-label">Most Lopsided Win</span>
              <span className="lor-rivalry-notable-val">
                {rivalry.biggest_win.winner} · {rivalry.biggest_win.score}
                <span style={{ color: '#666', marginLeft: 8 }}>+{rivalry.biggest_win.margin} pts · {rivalry.biggest_win.season}</span>
              </span>
            </div>
          )}
          {rivalry.closest_game && (
            <div className="lor-rivalry-notable-row">
              <span className="lor-history-section-label">Closest Game</span>
              <span className="lor-rivalry-notable-val">
                {rivalry.closest_game.score}
                <span style={{ color: '#666', marginLeft: 8 }}>{rivalry.closest_game.margin} pts · {rivalry.closest_game.season}</span>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function LORRivalries({ api }) {
  const [rivalries, setRivalries] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  useEffect(() => {
    fetch(`${api}/api/lor/rivalries`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(data => { setRivalries(data || []); setLoading(false); })
      .catch(e => { setError('Rivalries data unavailable.'); setLoading(false); });
  }, [api]);

  if (loading) return <div className="lor-loading">Loading rivalry engine…</div>;
  if (error)   return <div className="lor-empty">{error}</div>;

  const personal  = rivalries.filter(r => r.intensity_score >= 80);
  const heated    = rivalries.filter(r => r.intensity_score >= 60 && r.intensity_score < 80);
  const active    = rivalries.filter(r => r.intensity_score >= 35 && r.intensity_score < 60);
  const developing = rivalries.filter(r => r.intensity_score < 35);

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title">🔥 Rivalry Engine</h2>
        <div className="lor-page-meta">
          {rivalries.length} rivalry pair{rivalries.length !== 1 ? 's' : ''} detected · all-time
        </div>
      </div>

      {rivalries.length === 0 && (
        <div className="lor-empty">
          No rivalries detected yet. More history data needed.
        </div>
      )}

      {personal.length > 0 && (
        <div className="lor-rivalry-tier-section">
          <div className="lor-rivalry-tier-header" style={{ color: '#f87171' }}>💢 Personal Rivalries</div>
          {personal.map(r => <RivalryCard key={r.key} rivalry={r} />)}
        </div>
      )}
      {heated.length > 0 && (
        <div className="lor-rivalry-tier-section">
          <div className="lor-rivalry-tier-header" style={{ color: '#fb923c' }}>🔥 Heated Rivalries</div>
          {heated.map(r => <RivalryCard key={r.key} rivalry={r} />)}
        </div>
      )}
      {active.length > 0 && (
        <div className="lor-rivalry-tier-section">
          <div className="lor-rivalry-tier-header" style={{ color: '#60a5fa' }}><Icon emoji="⚔️" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Active Rivalries</div>
          {active.map(r => <RivalryCard key={r.key} rivalry={r} />)}
        </div>
      )}
      {developing.length > 0 && (
        <div className="lor-rivalry-tier-section">
          <div className="lor-rivalry-tier-header" style={{ color: '#9ca3af' }}>👀 Developing</div>
          {developing.map(r => <RivalryCard key={r.key} rivalry={r} />)}
        </div>
      )}

      <div className="lor-history-note">
        Rivalries auto-detected from full match history. Criteria: 10+ matchups, multiple playoff meetings,
        close all-time record, or championship game meeting.
      </div>
    </div>
  );
}
